/**
 * IDDLAIEvaluate — Phase 2 · AI Evaluation primitive renderer.
 *
 * Purpose: elicit AI Evaluation competency by presenting a completed
 * AI output plus its sources and requiring the candidate to
 * INSPECT, VERIFY, COMPARE, IDENTIFY concerns, REQUEST verification,
 * DECIDE reliance, and reach a final decision (accept · revise ·
 * reject · request_human_review). The candidate does NOT author the
 * prompt that produced the output — the observable competency is
 * evaluation depth, not prompt craft.
 *
 * Scenario schema (all content comes from concept.scenario_text —
 * no Kauri Point or domain content is embedded in this file):
 *
 *   {
 *     task_brief: string,
 *     ai_output: {
 *       title: string,
 *       body: string,
 *       claims: [
 *         { id, statement, supported_by: [source_id], uncertain?, contested? }
 *       ],
 *       sources: [{ id, label, note (credibility caveat) }]
 *     },
 *     comparison_output?: { title, body }
 *   }
 *
 * Emitted events (candidate-attributed only; the recorder posts
 * with actor="candidate" by default):
 *
 *   evaluate_workspace_opened   — mount
 *   ai_output_viewed            — output panel opened
 *   source_viewed               — { source_id }
 *   claim_evidence_checked      — { claim_id, verdict:"supported"|"weak"|"missing" }
 *   output_comparison_opened
 *   concern_flagged             — { claim_id?, source_id?, reason }
 *   verification_requested      — { target: "claim"|"source", id }
 *   reliance_decision_made      — { decision: "rely"|"rely_with_verification"|"do_not_rely" }
 *   final_decision              — { decision: "accept"|"revise"|"reject"|"request_human_review" }
 *
 * Scoring is the contract's job. This primitive only emits.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, FileSearch, GitCompare, Flag, ShieldCheck, CheckCircle2, RefreshCcw, XCircle, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function IDDLAIEvaluate({
  attemptId,
  conceptId,
  scenarioText,
  onEventLogged,
}) {
  const taskBrief = scenarioText?.task_brief || "Evaluate the AI-generated output on the right. Decide whether it can be relied upon.";
  const aiOutput = scenarioText?.ai_output || null;
  const comparison = scenarioText?.comparison_output || null;

  const claims = aiOutput?.claims || [];
  const sources = aiOutput?.sources || [];
  const sourceById = useMemo(() => {
    const m = {};
    for (const s of sources) m[s.id] = s;
    return m;
  }, [sources]);

  const [expandedClaim, setExpandedClaim] = useState(null);
  const [expandedSource, setExpandedSource] = useState(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [reliance, setReliance] = useState(null);
  const [finalized, setFinalized] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flags, setFlags] = useState([]); // {kind, id, reason}
  const [verifications, setVerifications] = useState([]); // {target, id}

  const openedRef = useRef(false);
  const outputSeenRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    emit("evaluate_workspace_opened", null, "evaluate_home");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function emit(eventName, eventValue, screenContext) {
    try {
      const r = await api
        .post(`/attempts/${attemptId}/events`, {
          concept_id: conceptId,
          event_name: eventName,
          event_value: eventValue,
          screen_context: screenContext,
          client_ts_ms: Date.now(),
        })
        .then((x) => x.data);
      if (onEventLogged && r?.event) onEventLogged(r.event);
      return r?.event;
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Event failed";
      toast.error(`Event ${eventName} rejected: ${detail}`);
      return null;
    }
  }

  const inspectOutput = () => {
    if (outputSeenRef.current) return;
    outputSeenRef.current = true;
    emit("ai_output_viewed", null, "ai_output");
  };

  const viewSource = (src) => {
    setExpandedSource(src.id === expandedSource ? null : src.id);
    if (src.id !== expandedSource) emit("source_viewed", { source_id: src.id }, "sources");
  };

  const checkClaim = (claim, verdict) => {
    emit("claim_evidence_checked", { claim_id: claim.id, verdict }, "claims");
    setExpandedClaim(claim.id);
  };

  const openComparison = () => {
    setComparisonOpen(true);
    emit("output_comparison_opened", null, "compare");
  };

  const flagConcern = (kind, id, reason) => {
    setFlags((xs) => [...xs, { kind, id, reason }]);
    emit("concern_flagged", { [`${kind}_id`]: id, reason }, "flag");
  };

  const requestVerification = (target, id) => {
    setVerifications((xs) => [...xs, { target, id }]);
    emit("verification_requested", { target, id }, "verify");
  };

  const setRelianceAndEmit = (decision) => {
    setReliance(decision);
    emit("reliance_decision_made", { decision }, "reliance");
  };

  const finalize = async (decision) => {
    if (busy) return;
    setBusy(true);
    try {
      await emit(
        "final_decision",
        { decision, reliance, flags_count: flags.length, verifications_count: verifications.length },
        "finalize",
      );
      setFinalized(decision);
      toast.success(`Recorded final decision: ${decision}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="iddl-ai-evaluate">
      {/* Left: task brief + reliance + finalize */}
      <section className="border border-[var(--iddl-rule)] bg-white">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <FileSearch size={14} />
          <span className="font-display font-semibold">Evaluation task</span>
        </header>
        <div className="p-3 space-y-3">
          <p className="text-sm leading-6 whitespace-pre-wrap" data-testid="evaluate-task-brief">
            {taskBrief}
          </p>

          <div className="space-y-2">
            <div className="label-mono text-xs">Reliance decision</div>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "rely", label: "Rely on output", cls: "border-emerald-400" },
                { id: "rely_with_verification", label: "Rely — after verification", cls: "border-amber-400" },
                { id: "do_not_rely", label: "Do not rely", cls: "border-rose-400" },
              ].map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRelianceAndEmit(r.id)}
                  data-testid={`evaluate-reliance-${r.id}`}
                  className={`text-xs px-3 py-1.5 border ${r.cls} ${reliance === r.id ? "bg-zinc-100 font-semibold" : "bg-white"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2 border-t border-[var(--iddl-rule)] pt-3">
            <div className="label-mono text-xs">Final decision</div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" disabled={busy || !!finalized}
                onClick={() => finalize("accept")}
                data-testid="evaluate-final-accept">
                <CheckCircle2 size={14} className="me-1" /> Accept
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !!finalized}
                onClick={() => finalize("revise")}
                data-testid="evaluate-final-revise">
                <RefreshCcw size={14} className="me-1" /> Revise
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !!finalized}
                onClick={() => finalize("reject")}
                data-testid="evaluate-final-reject">
                <XCircle size={14} className="me-1" /> Reject
              </Button>
              <Button size="sm" variant="outline" disabled={busy || !!finalized}
                onClick={() => finalize("request_human_review")}
                data-testid="evaluate-final-human">
                <UserCog size={14} className="me-1" /> Human review
              </Button>
            </div>
            {finalized ? (
              <div className="text-xs text-emerald-800 border-t border-[var(--iddl-rule)] pt-2">
                Recorded: <strong>{finalized}</strong>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* Right: AI output with claims + sources */}
      <section className="border border-[var(--iddl-rule)] bg-white" data-testid="evaluate-ai-output">
        <header className="px-3 py-2 border-b border-[var(--iddl-rule)] flex items-center gap-2 text-sm">
          <Eye size={14} />
          <span className="font-display font-semibold">{aiOutput?.title || "AI output"}</span>
          {comparison ? (
            <button
              type="button"
              onClick={openComparison}
              data-testid="evaluate-open-comparison"
              className="ms-auto text-xs px-2 py-1 border border-[var(--iddl-rule)]"
            >
              <GitCompare size={12} className="inline me-1" /> Compare
            </button>
          ) : null}
        </header>
        <div className="p-3 space-y-3" onClick={inspectOutput}>
          {aiOutput?.body ? (
            <div className="text-sm leading-6 whitespace-pre-wrap" data-testid="evaluate-output-body">
              {aiOutput.body}
            </div>
          ) : (
            <div className="text-xs text-zinc-500 italic">
              No AI output supplied in scenario_text.ai_output. The evaluate primitive
              needs a body, claims and sources to elicit competency.
            </div>
          )}

          {claims.length > 0 ? (
            <div className="space-y-1.5 border-t border-[var(--iddl-rule)] pt-2">
              <div className="label-mono text-xs">Claims</div>
              {claims.map((c) => (
                <div key={c.id} className="border border-[var(--iddl-rule)]" data-testid={`evaluate-claim-${c.id}`}>
                  <div className="px-2 py-1.5 flex items-center gap-2 text-xs">
                    <span className="flex-1">{c.statement}</span>
                    <button type="button" onClick={() => checkClaim(c, "supported")}
                      data-testid={`evaluate-claim-${c.id}-supported`}
                      className="px-2 py-0.5 border border-emerald-400">supported</button>
                    <button type="button" onClick={() => checkClaim(c, "weak")}
                      data-testid={`evaluate-claim-${c.id}-weak`}
                      className="px-2 py-0.5 border border-amber-400">weak</button>
                    <button type="button" onClick={() => checkClaim(c, "missing")}
                      data-testid={`evaluate-claim-${c.id}-missing`}
                      className="px-2 py-0.5 border border-rose-400">missing</button>
                  </div>
                  {expandedClaim === c.id && (c.supported_by || []).length > 0 ? (
                    <div className="px-2 py-1 border-t border-[var(--iddl-rule)] text-xs bg-[var(--iddl-paper)]">
                      Sources supporting: {(c.supported_by || []).map((sid) => sourceById[sid]?.label || sid).join(", ")}
                    </div>
                  ) : null}
                  <div className="px-2 py-1 border-t border-[var(--iddl-rule)] flex gap-2 text-xs">
                    <button type="button"
                      onClick={() => flagConcern("claim", c.id, "unclear evidence")}
                      data-testid={`evaluate-flag-claim-${c.id}`}
                      className="inline-flex items-center gap-1 border border-rose-300 px-2 py-0.5">
                      <Flag size={10} /> flag concern
                    </button>
                    <button type="button"
                      onClick={() => requestVerification("claim", c.id)}
                      data-testid={`evaluate-verify-claim-${c.id}`}
                      className="inline-flex items-center gap-1 border border-zinc-300 px-2 py-0.5">
                      <ShieldCheck size={10} /> request verification
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {sources.length > 0 ? (
            <div className="space-y-1.5 border-t border-[var(--iddl-rule)] pt-2">
              <div className="label-mono text-xs">Sources</div>
              {sources.map((s) => (
                <div key={s.id} className="border border-[var(--iddl-rule)]" data-testid={`evaluate-source-${s.id}`}>
                  <button type="button" onClick={() => viewSource(s)}
                    data-testid={`evaluate-source-${s.id}-view`}
                    className="w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 hover:bg-zinc-50">
                    <Eye size={10} />
                    <span className="flex-1">{s.label}</span>
                    <span className="label-mono">{s.id}</span>
                  </button>
                  {expandedSource === s.id ? (
                    <div className="px-2 py-1 border-t border-[var(--iddl-rule)] text-xs bg-[var(--iddl-paper)]">
                      {s.note || "(no credibility note supplied)"}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {comparisonOpen && comparison ? (
            <div className="space-y-1 border-t border-[var(--iddl-rule)] pt-2" data-testid="evaluate-comparison-panel">
              <div className="label-mono text-xs">Comparison output — {comparison.title}</div>
              <div className="text-xs leading-5 whitespace-pre-wrap">{comparison.body}</div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
